import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col, Card, Select, Button, Typography, Space, Tooltip, message } from 'antd';
import { 
	BgColorsOutlined, 
	SettingOutlined, 
	SaveOutlined,
	ReloadOutlined,
	GlobalOutlined
} from '@ant-design/icons';
import { CloudMoon, CloudSun } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useI18n } from '../../hooks/useI18n';
import SecuritySettings from '../../components/security-settings/SecuritySettings';
import DashboardPassword from '../../components/dashboard-password/DashboardPassword';
import './Settings.scss';

const { Title, Text } = Typography;
const { Option } = Select;

// Predefined theme colors
const THEME_COLORS = [
	{ name: 'Blue', value: '#1890ff', secondary: '#40a9ff' },
	{ name: 'Green', value: '#52c41a', secondary: '#73d13d' },
	{ name: 'Purple', value: '#722ed1', secondary: '#9254de' },
	{ name: 'Orange', value: '#fa8c16', secondary: '#ffa940' },
	{ name: 'Red', value: '#f5222d', secondary: '#ff4d4f' },
	{ name: 'Cyan', value: '#13c2c2', secondary: '#36cfc9' },
	{ name: 'Pink', value: '#eb2f96', secondary: '#f759ab' },
	{ name: 'Lime', value: '#a0d911', secondary: '#b7eb8f' },
	{ name: 'Volcano', value: '#fa541c', secondary: '#ff7a45' },
	{ name: 'Gold', value: '#faad14', secondary: '#ffc53d' },
];

const Settings = () => {
	const { theme, toggleTheme, themeColor, setThemeColor } = useTheme();
	const { t, changeLanguage, currentLanguage, languages } = useI18n();
	const location = useLocation();
	const [isDark, setIsDark] = useState(theme === 'dark');
	const [selectedColor, setSelectedColor] = useState(themeColor);
	const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
	const [hasChanges, setHasChanges] = useState(false);

	useEffect(() => {
		setIsDark(theme === 'dark');
		setSelectedColor(themeColor);
		setSelectedLanguage(currentLanguage);
	}, [theme, themeColor, currentLanguage]);

	// Scroll to dashboard password section when navigated with hash
	useEffect(() => {
		if (location && location.hash === '#dashboard-password') {
			const el = document.getElementById('dashboard-password');
			if (el) {
				setTimeout(() => {
					el.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}, 200);
			}
		}
	}, [location]);

	useEffect(() => {
		const hasThemeChange = (theme === 'dark') !== isDark;
		const hasColorChange = themeColor !== selectedColor;
		const hasLanguageChange = currentLanguage !== selectedLanguage;
		setHasChanges(hasThemeChange || hasColorChange || hasLanguageChange);
	}, [isDark, selectedColor, selectedLanguage, theme, themeColor, currentLanguage]);

	const handleThemeToggle = (checked) => {
		setIsDark(checked);
	};

	const handleColorChange = (color) => {
		setSelectedColor(color);
	};

	const handleLanguageChange = (lang) => {
		setSelectedLanguage(lang);
	};

	const handleSave = () => {
		// Apply theme mode
		if ((theme === 'dark') !== isDark) {
			toggleTheme();
		}
		
		// Apply color theme
		if (themeColor !== selectedColor) {
			setThemeColor(selectedColor);
		}

		// Apply language
		if (currentLanguage !== selectedLanguage) {
			changeLanguage(selectedLanguage);
		}

		setHasChanges(false);
		message.success(t('settings.save_settings') + ' ' + t('common.success'));
	};

	const handleReset = () => {
		setIsDark(theme === 'dark');
		setSelectedColor(themeColor);
		setSelectedLanguage(currentLanguage);
		setHasChanges(false);
		message.info(t('common.reset') + ' ' + t('common.success'));
	};

	const activeColor = THEME_COLORS.find((color) => color.value === selectedColor) || THEME_COLORS[0];
	const currentColor = THEME_COLORS.find((color) => color.value === themeColor) || activeColor;
	const languageLabel = languages.find((lang) => lang.code === selectedLanguage);
	const currentThemeLabel = isDark ? t('settings.dark_mode') : t('settings.light_mode');

	const ColorPreview = ({ color, isSelected, onClick }) => (
		<button
			type="button"
			className={`color-preview ${isSelected ? 'selected' : ''}`}
			style={{
				background: `linear-gradient(145deg, ${color.secondary} 0%, ${color.value} 100%)`
			}}
			onClick={() => onClick(color.value)}
			title={color.name}
		>
			<span className="color-preview__label">{color.name}</span>
			{isSelected && <div className="check-mark">✓</div>}
		</button>
	);

	return (
		<div className={`settings-page ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
			<div className="settings-shell">
				<div className="settings-hero">
					<div className="settings-hero__content">
						<div className="settings-hero__eyebrow">{t('settings.general_settings')}</div>
						<Title level={2}>
							<SettingOutlined /> {t('settings.title')}
						</Title>
						<Text>
							{t('settings.preview_description')}
						</Text>
					</div>
					<div className="settings-hero__stats">
						<div className="settings-hero__stat">
							<span>{t('settings.current_theme')}</span>
							<strong>{theme === 'dark' ? t('settings.dark') : t('settings.light')}</strong>
						</div>
						<div className="settings-hero__stat">
							<span>{t('settings.primary_color')}</span>
							<strong>{currentColor.name}</strong>
						</div>
						<div className="settings-hero__stat">
							<span>{t('settings.language')}</span>
							<strong>{languageLabel?.nativeName || selectedLanguage}</strong>
						</div>
					</div>
				</div>

				<Row gutter={[24, 24]}>
				<Col span={24}>
					<SecuritySettings />
				</Col>

				<Col span={24}>
					<DashboardPassword />
				</Col>

				<Col xl={15} lg={15} md={24} sm={24}>
					<Card className="settings-card" bordered={false}>
						<div className="settings-stack">
							<div className="setting-section setting-section--feature">
								<div className="setting-header setting-header--split">
									<Title level={4}>
										<GlobalOutlined /> {t('settings.language')}
									</Title>
									<Text type="secondary">
										{t('settings.language_description')}
									</Text>
								</div>
								<div className="setting-control">
									<Select
										value={selectedLanguage}
										onChange={handleLanguageChange}
										className="settings-select"
										placeholder={t('settings.select_language')}
									>
										{languages.map((lang) => (
											<Option key={lang.code} value={lang.code}>
												{lang.nativeName} ({lang.name})
											</Option>
										))}
									</Select>
									<div className="setting-note">
										<span className="setting-note__label">{t('settings.selected')}</span>
										<strong>{languageLabel?.nativeName || selectedLanguage}</strong>
									</div>
								</div>
							</div>

							<div className="setting-section setting-section--feature">
								<div className="setting-header setting-header--split">
									<Title level={4}>
										{isDark ? <CloudMoon size={18} /> : <CloudSun size={18} />} {t('settings.theme_settings')}
									</Title>
									<Text type="secondary">
										{t('settings.theme_description')}
									</Text>
								</div>
								<div className="setting-control theme-toggle-control setting-control--surface">
									<div>
										<div className="switch-label">{currentThemeLabel}</div>
										<Text type="secondary">
											{t('settings.preview')}: {activeColor.name}
										</Text>
									</div>
									<Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
										<button
											type="button"
											className={`theme-cloud-toggle ${isDark ? 'is-dark' : 'is-light'}`}
											onClick={() => handleThemeToggle(!isDark)}
											aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
										>
											<span className="theme-cloud-track">
												<span className="theme-cloud-glow" />
												<span className="theme-cloud-icon">
													{isDark ? <CloudMoon size={18} /> : <CloudSun size={18} />}
												</span>
											</span>
										</button>
									</Tooltip>
								</div>
							</div>

							<div className="setting-section setting-section--feature">
								<div className="setting-header setting-header--split">
									<Title level={4}>
										<BgColorsOutlined /> {t('settings.theme_color')}
									</Title>
									<Text type="secondary">
										{t('settings.color_scheme_description')}
									</Text>
								</div>
								<div className="setting-control">
									<div className="color-grid">
										{THEME_COLORS.map((color) => (
											<ColorPreview
												key={color.value}
												color={color}
												isSelected={selectedColor === color.value}
												onClick={handleColorChange}
											/>
										))}
									</div>
									<div className="color-info">
										<div>
											<Text strong>
												{t('settings.selected')}: {activeColor.name}
											</Text>
											<Text type="secondary"> {selectedColor}</Text>
										</div>
										<div
											className="current-color-display"
											style={{ background: `linear-gradient(145deg, ${activeColor.secondary} 0%, ${activeColor.value} 100%)` }}
										/>
									</div>
								</div>
							</div>

							<div className="setting-section setting-section--preview">
								<div className="setting-header setting-header--split">
									<Title level={4}>{t('settings.preview')}</Title>
									<Text type="secondary">
										{t('settings.preview_description')}
									</Text>
								</div>
								<div
									className="theme-preview"
									style={{
										'--preview-accent': activeColor.value,
										'--preview-accent-soft': activeColor.secondary,
										'--preview-surface': isDark ? '#141414' : '#ffffff',
										'--preview-text': isDark ? '#ffffff' : '#111827',
										'--preview-border': isDark ? '#434343' : '#d9d9d9'
									}}
								>
									<div className="preview-header">
										<Text style={{ color: 'white' }} strong>{t('settings.sample_header')}</Text>
										<span className="preview-chip">{currentThemeLabel}</span>
									</div>
									<div className="preview-content">
										<div className="preview-stat">
											<strong>24</strong>
											<span>{t('settings.primary_button')}</span>
										</div>
										<div className="preview-stat muted">
											<strong>Aa</strong>
											<span>{languageLabel?.nativeName || selectedLanguage}</span>
										</div>
									</div>
									<div className="preview-actions">
										<Button type="primary" style={{ backgroundColor: activeColor.value, borderColor: activeColor.value }}>
											{t('settings.primary_button')}
										</Button>
										<Button>{t('settings.secondary_button')}</Button>
									</div>
								</div>
							</div>

							<div className="setting-actions">
								<div className="setting-actions__text">
									<Text strong>{hasChanges ? t('settings.save_changes') : t('common.success')}</Text>
									<Text type="secondary">
										{hasChanges ? t('settings.storage_info') : t('settings.preview_description')}
									</Text>
								</div>
								<Space>
									<Button
										icon={<ReloadOutlined />}
										onClick={handleReset}
										disabled={!hasChanges}
									>
										{t('settings.reset')}
									</Button>
									<Button
										type="primary"
										icon={<SaveOutlined />}
										onClick={handleSave}
										disabled={!hasChanges}
										style={{ backgroundColor: activeColor.value, borderColor: activeColor.value }}
									>
										{t('settings.save_changes')}
									</Button>
								</Space>
							</div>
						</div>
					</Card>
				</Col>

				<Col xl={9} lg={9} md={24} sm={24}>
					<Space direction="vertical" size={24} style={{ width: '100%' }}>
						<Card className="info-card summary-card" bordered={false}>
							<Space direction="vertical" size="middle" style={{ width: '100%' }}>
								<Title level={4}>{t('settings.theme_information')}</Title>
								<div className="summary-pill-row">
									<div className="summary-pill">
										<span>{t('settings.current_theme')}</span>
										<strong>{currentThemeLabel}</strong>
									</div>
									<div className="summary-pill">
										<span>{t('settings.language')}</span>
										<strong>{languageLabel?.nativeName || selectedLanguage}</strong>
									</div>
								</div>
							<div className="info-item">
								<Text strong>{t('settings.current_theme')}:</Text>
								<Text> {theme === 'dark' ? t('settings.dark') : t('settings.light')}</Text>
							</div>
							<div className="info-item">
								<Text strong>{t('settings.primary_color')}:</Text>
								<Text> {currentColor.name}</Text>
							</div>
							<div className="info-item">
								<Text strong>{t('settings.language')}:</Text>
								<Text> {languageLabel?.nativeName || selectedLanguage}</Text>
							</div>
							<div className="info-item">
								<Text strong>{t('settings.storage')}:</Text>
								<Text> {t('settings.local_storage')}</Text>
							</div>
							<Text type="secondary" style={{ fontSize: '12px' }}>
								{t('settings.storage_info')}
							</Text>
							</Space>
						</Card>

						<Card className="info-card spotlight-card" bordered={false}>
							<div className="spotlight-card__badge">{t('settings.preview')}</div>
							<Title level={4}>{activeColor.name}</Title>
							<Text type="secondary">{t('settings.color_scheme_description')}</Text>
							<div className="spotlight-card__swatch" style={{ background: `linear-gradient(145deg, ${activeColor.secondary} 0%, ${activeColor.value} 100%)` }} />
						</Card>
					</Space>
				</Col>
			</Row>
			</div>
		</div>
	);
};

export default Settings;